import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationAutocomplete from './MobileLocationAutocomplete';
import PhoneNumberInput from './MobilePhoneNumberInput';
import Popup from './MobilePopup';
import MobileCoverLetterGen from './MobileCoverLetterGen';
import '../styles/mobile-editpanel.css';
import { 
  FaUser, 
  FaFileAlt, 
  FaBriefcase, 
  FaTools, 
  FaGraduationCap, 
  FaBook, 
  FaChevronDown, 
  FaChevronUp, 
  FaCheckCircle 
} from 'react-icons/fa';

const CharacterCount = ({ current, max }) => {
  const isNearLimit = max && current >= max * 0.9;
  const isOverLimit = max && current > max;
  return (
    <div className={`character-count ${isOverLimit ? 'danger' : isNearLimit ? 'warning' : ''}`}>
      {current}{max ? ` / ${max}` : ''} characters
    </div>
  );
};

const MobileEditPanel = ({
  name = '',
  title = '',
  location = '',
  email = '',
  phone = '',
  summary = '',
  experience = '',
  skills = '',
  education = '',
  publications = '',
  onInputChange,
  onGenerateClick,
  isLoading = false,
  statusMessage = '',
  characterLimits = {},
  activeSection = '',
  toggleSection,
  isEditPanelOpen, // Prop to control visibility
  onClose, // Prop to close the panel
  darkMode, // Added darkMode prop
  user,
  tier,
  credits,
  onShowCreditGate,
  formData = {},
  handleCoverLetterGenerate,
  fetchSubscription
}) => {
  const navigate = useNavigate();



  const hasCoverLetterAccess = user && (tier === 'professional' || tier === 'ultimate');
  const [locationLocked, setLocationLocked] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showClearButton, setShowClearButton] = useState({});
  const inputRefs = useRef({});
  const panelRef = useRef(null);
  const sectionRefs = useRef({});

  // Auto-snap scroll effect on activeSection change
  useEffect(() => {
    if (activeSection && sectionRefs.current[activeSection]) {
      const element = sectionRefs.current[activeSection];
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250); // Delay allows drawer to finish visual sizing setup
    }
  }, [activeSection]);

  const isFieldValid = (fieldName, val) => {
    if (!val) return false;
    const cleanVal = Array.isArray(val) ? val.join(' ').trim() : val.trim();
    if (cleanVal.length === 0) return false;
    
    if (fieldName === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanVal);
    }
    if (fieldName === 'phone') {
      return cleanVal.replace(/[^0-9]/g, '').length >= 7;
    }
    if (['name', 'title', 'location'].includes(fieldName)) {
      return cleanVal.length >= 2;
    }
    return cleanVal.length >= 10;
  };

  const getSectionOfField = (field) => {
    if (!field) return null;
    if (['name', 'title', 'email', 'location', 'phone'].includes(field)) return 'contact';
    if (['jobDescription', 'companyName', 'address', 'hiringManagerName', 'hiringManagerJobTitle', 'coverLetterBody'].includes(field)) return 'coverLetter';
    return field;
  };

  const handleFocus = useCallback((name) => {
    setActiveField(name);
  }, []);

  const handleBlur = useCallback(() => {
    setActiveField(null);
  }, []);

  const getTextWidth = (text, font) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font || '16px Arial';
    return context.measureText(text).width;
  };

  const calculateInputWidth = useCallback((inputElement) => {
    const text = inputElement.value || inputElement.placeholder;
    const style = window.getComputedStyle(inputElement);
    const font = `${style.fontSize} ${style.fontFamily}`;
    const textWidth = getTextWidth(text, font);
    const padding = 108;
    const minWidth = text.length > 10 ? 153 : 108;
    return Math.max(textWidth + padding, minWidth);
  }, []);

  const autoResizeTextarea = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.width = '100%';
      if (textarea.rows === 1) {
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 40)}px`;
      }
    }
  }, []);

  const updateInputWidths = useCallback(() => {
    let maxWidth = 0;
    Object.keys(inputRefs.current).forEach(key => {
      const input = inputRefs.current[key];
      if (input) {
        const width = calculateInputWidth(input);
        if (width > maxWidth) {
          maxWidth = width;
        }
      }
    });
    Object.keys(inputRefs.current).forEach(key => {
      const input = inputRefs.current[key];
      if (input) {
        input.style.width = `${maxWidth}px`;
      }
    });
  }, [calculateInputWidth]);

  useEffect(() => {
    updateInputWidths();

    const handleResize = () => updateInputWidths();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateInputWidths, name, title, location, email, phone]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    if (value.length > characterLimits[name]) {
      return;
    }

    let formattedValue = value;

    if (name === 'experience' || name === 'skills' || name === 'name' || name === 'title' || name === 'email') {
      formattedValue = value.split('\n');
    }

    onInputChange({ target: { name, value: formattedValue } });

    const input = inputRefs.current[name];
    if (input) {
      autoResizeTextarea(input);
    }

    setShowClearButton(prev => ({
      ...prev,
      [name]: value.length > 0
    }));

    // If the input is for location, update the LocationAutocomplete component
    if (name === 'location') {
      setLocationLocked(value.length > 0);
    }
  }, [onInputChange, autoResizeTextarea, characterLimits]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  }, []);

  const handleLocationSelect = useCallback((selectedLocation) => {
    onInputChange({ target: { name: 'location', value: selectedLocation } });
    setLocationLocked(true);
    updateInputWidths();
  }, [onInputChange, updateInputWidths]);

  const unlockLocationField = useCallback(() => {
    setLocationLocked(false);
  }, []);

  const activeSectionsOrdered = ['contact', 'summary', 'experience', 'skills', 'education', 'publications', 'coverLetter'];
  
  const renderSectionNavFooter = (currentSection) => {
    const currentIndex = activeSectionsOrdered.indexOf(currentSection);
    const prevSection = currentIndex > 0 ? activeSectionsOrdered[currentIndex - 1] : null;
    const nextSection = currentIndex < activeSectionsOrdered.length - 1 ? activeSectionsOrdered[currentIndex + 1] : null;
    
    return (
      <div className="section-nav-footer">
        {prevSection ? (
          <button 
            type="button" 
            className="section-nav-btn prev-btn" 
            onClick={(e) => {
              e.stopPropagation();
              toggleSection(prevSection);
            }}
          >
            ← Previous
          </button>
        ) : <div />}
        {nextSection ? (
          <button 
            type="button" 
            className="section-nav-btn next-btn" 
            onClick={(e) => {
              e.stopPropagation();
              toggleSection(nextSection);
            }}
          >
            Next Section →
          </button>
        ) : (
          <button 
            type="button" 
            className="section-nav-btn finish-btn" 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            ✓ Done Editing
          </button>
        )}
      </div>
    );
  };

  const renderSection = useCallback((sectionName, label, value, placeholder, isMultiLine = false, showGenerateButton = false, isContactField = false) => (
    <div className={`input-group ${isContactField ? 'contact-field' : ''}`}>
      <div className="label-container">
        <label htmlFor={sectionName}>{label}</label>
      </div>
      <div className={`input-container ${activeField === sectionName ? 'active' : ''}`}>
        <div className={`input-wrapper ${darkMode ? 'dark-mode' : ''}`}>
          {isMultiLine ? (
            <textarea
              ref={(el) => {
                inputRefs.current[sectionName] = el;
                autoResizeTextarea(el);
              }}
              name={sectionName}
              value={Array.isArray(value) ? value.join('\n') : value}
              onChange={(e) => {
                handleInputChange(e);
                autoResizeTextarea(e.target);
              }}
              onFocus={(e) => {
                handleFocus(sectionName);
                const target = e.target;
                setTimeout(() => {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              rows={4}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="input-field-style"
            />
          ) : (
            <input
              ref={(el) => (inputRefs.current[sectionName] = el)}
              name={sectionName}
              value={value}
              onChange={handleInputChange}
              onFocus={(e) => {
                handleFocus(sectionName);
                const target = e.target;
                setTimeout(() => {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="input-field-style"
            />
          )}
          {showClearButton[sectionName] && activeField === sectionName && (
            <button
              type="button"
              className={`clear-input-button ${showClearButton[sectionName] ? 'visible' : ''}`}
              onClick={() => {
                onInputChange({ target: { name: sectionName, value: '' } });
                setShowClearButton(prev => ({ ...prev, [sectionName]: false }));
              }}
              aria-label="Clear input"
            ></button>
          )}
          {activeField !== sectionName && isFieldValid(sectionName, value) && (
            <span className="input-validation-badge">✓</span>
          )}
        </div>
      </div>
      <div className="section-footer-row">
        <CharacterCount current={Array.isArray(value) ? value.join('').length : value.length} max={characterLimits[sectionName]} />
        {statusMessage && <div className="status-message">{statusMessage}</div>}
      </div>
      {showGenerateButton && (
        <button 
          className="section-generate-btn"
          onClick={() => onGenerateClick(sectionName)} 
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner"></div>
              <span>Generating {label}...</span>
            </>
          ) : (
            <>
              <span>⚡ Auto-Generate {label}</span>
            </>
          )}
        </button>
      )}
    </div>
  ), [activeField, autoResizeTextarea, handleBlur, handleFocus, handleInputChange, handleKeyDown, isLoading, onGenerateClick, onInputChange, statusMessage, characterLimits, showClearButton, darkMode]);

  useEffect(() => {
    const initialShowClearButton = {
      name: name.length > 0,
      title: title.length > 0,
      location: location.length > 0,
      email: email.length > 0,
      phone: phone.length > 0,
      summary: summary.length > 0,
      experience: experience.length > 0,
      skills: skills.length > 0,
      education: education.length > 0,
      publications: publications.length > 0
    };
    setShowClearButton(initialShowClearButton);
  }, [name, title, location, email, phone, summary, experience, skills, education, publications]);

  const sectionsToCheck = [
    name, title, location, email, phone, summary, experience, skills, education, publications
  ];
  const completedCount = sectionsToCheck.filter(val => {
    if (Array.isArray(val)) {
      return val.some(item => item && item.trim().length > 0);
    }
    return val && val.trim().length > 0;
  }).length;
  const progressPercent = Math.round((completedCount / sectionsToCheck.length) * 100);

  return (
    <>
      <div className={`mobile-edit-panel ${isEditPanelOpen ? 'open' : 'closing'}`} ref={panelRef}>
        <div className="mobile-edit-panel-header">
          <div className="mobile-edit-panel-title-container">
            <h2 className="mobile-edit-panel-title">Edit Resume</h2>
            <span className="save-status-indicator">● Auto-Saved</span>
          </div>
        </div>
        <div className="mobile-edit-panel-content">
          <div className="completion-progress-container">
            <div className="progress-bar-header">
              <span className="progress-title">Profile Completion</span>
              <span className="progress-value">{progressPercent}%</span>
            </div>
            <div className="progress-bar-track">
              <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
            </div>
          </div>
          {/* Contact Details Section */}
          <div 
            ref={el => sectionRefs.current['contact'] = el}
            className={`editor-accordion-card ${activeSection === 'contact' ? 'active' : ''} ${getSectionOfField(activeField) === 'contact' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('contact')}
          >
            <div className="accordion-left">
              <FaUser className="accordion-icon" />
              <div className="accordion-text">
                <h2>Contact Details</h2>
                <span className="accordion-desc">Name, title, email, phone & location</span>
              </div>
            </div>
            <div className="accordion-right">
              {name && email ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'contact' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'contact' && (
            <div className="section-content">
              <div className="contact-details-container">
                {renderSection('name', 'Name', name, 'Enter your name', false, false, true)}
                {renderSection('title', 'Title', title, 'Enter your title', false, true, true)}
                {renderSection('email', 'Email', email, 'Enter your email', false, false, true)}
                <div className="input-group contact-field">
                  <div className="label-container">
                    <label htmlFor="location">Location</label>
                  </div>
                  <LocationAutocomplete
                    onLocationSelect={handleLocationSelect}
                    lockLocationField={() => setLocationLocked(true)}
                    unlockLocationField={unlockLocationField}
                    locationLocked={locationLocked}
                    darkMode={darkMode}
                    currentLocation={location}
                    value={location}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('location')}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    inputRef={(el) => (inputRefs.current['location'] = el)}
                  />
                </div>
                <div className="input-group contact-field">
                  <div className="label-container">
                    <label htmlFor="phone">Phone</label>
                  </div>
                  <PhoneNumberInput
                    value={phone}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('phone')}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    inputRef={(el) => (inputRefs.current['phone'] = el)}
                    darkMode={darkMode}
                  />
                </div>
              </div>
              {renderSectionNavFooter('contact')}
            </div>
          )}

          {/* Summary Section */}
          <div 
            ref={el => sectionRefs.current['summary'] = el}
            className={`editor-accordion-card ${activeSection === 'summary' ? 'active' : ''} ${getSectionOfField(activeField) === 'summary' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('summary')}
          >
            <div className="accordion-left">
              <FaFileAlt className="accordion-icon" />
              <div className="accordion-text">
                <h2>Summary</h2>
                <span className="accordion-desc">Professional summary & objective statement</span>
              </div>
            </div>
            <div className="accordion-right">
              {summary ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'summary' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'summary' && (
            <div className="section-content">
              {renderSection('summary', 'Summary', summary, 'Enter your summary', true, true)}
              {renderSectionNavFooter('summary')}
            </div>
          )}

          {/* Experience Section */}
          <div 
            ref={el => sectionRefs.current['experience'] = el}
            className={`editor-accordion-card ${activeSection === 'experience' ? 'active' : ''} ${getSectionOfField(activeField) === 'experience' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('experience')}
          >
            <div className="accordion-left">
              <FaBriefcase className="accordion-icon" />
              <div className="accordion-text">
                <h2>Experience</h2>
                <span className="accordion-desc">Work history, achievements & milestones</span>
              </div>
            </div>
            <div className="accordion-right">
              {experience ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'experience' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'experience' && (
            <div className="section-content">
              {renderSection('experience', 'Experience', experience, 'Enter your experience', true, true)}
              {renderSectionNavFooter('experience')}
            </div>
          )}

          {/* Skills Section */}
          <div 
            ref={el => sectionRefs.current['skills'] = el}
            className={`editor-accordion-card ${activeSection === 'skills' ? 'active' : ''} ${getSectionOfField(activeField) === 'skills' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('skills')}
          >
            <div className="accordion-left">
              <FaTools className="accordion-icon" />
              <div className="accordion-text">
                <h2>Skills</h2>
                <span className="accordion-desc">Tech stack, tools & core competencies</span>
              </div>
            </div>
            <div className="accordion-right">
              {skills ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'skills' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'skills' && (
            <div className="section-content">
              {renderSection('skills', 'Skills', skills, 'Enter your skills', true, true)}
              {renderSectionNavFooter('skills')}
            </div>
          )}

          {/* Education Section */}
          <div 
            ref={el => sectionRefs.current['education'] = el}
            className={`editor-accordion-card ${activeSection === 'education' ? 'active' : ''} ${getSectionOfField(activeField) === 'education' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('education')}
          >
            <div className="accordion-left">
              <FaGraduationCap className="accordion-icon" />
              <div className="accordion-text">
                <h2>Education</h2>
                <span className="accordion-desc">Degrees, certifications & academic history</span>
              </div>
            </div>
            <div className="accordion-right">
              {education ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'education' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'education' && (
            <div className="section-content">
              {renderSection('education', 'Education', education, 'Enter your education', true, true)}
              {renderSectionNavFooter('education')}
            </div>
          )}

          {/* Publications Section */}
          <div 
            ref={el => sectionRefs.current['publications'] = el}
            className={`editor-accordion-card ${activeSection === 'publications' ? 'active' : ''} ${getSectionOfField(activeField) === 'publications' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('publications')}
          >
            <div className="accordion-left">
              <FaBook className="accordion-icon" />
              <div className="accordion-text">
                <h2>Publications</h2>
                <span className="accordion-desc">Research papers, articles & open source</span>
              </div>
            </div>
            <div className="accordion-right">
              {publications ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'publications' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'publications' && (
            <div className="section-content">
              {renderSection('publications', 'Publications', publications, 'Enter your publications', true, true)}
              {renderSectionNavFooter('publications')}
            </div>
          )}

          {/* Cover Letter Section */}
          <div 
            ref={el => sectionRefs.current['coverLetter'] = el}
            className={`editor-accordion-card ${activeSection === 'coverLetter' ? 'active' : ''} ${getSectionOfField(activeField) === 'coverLetter' ? 'active-editing' : ''}`} 
            onClick={() => toggleSection('coverLetter')}
          >
            <div className="accordion-left">
              <FaFileAlt className="accordion-icon" />
              <div className="accordion-text">
                <h2>Cover Letter</h2>
                <span className="accordion-desc">AI-tailored cover letter generator</span>
              </div>
            </div>
            <div className="accordion-right">
              {formData?.coverLetter ? <FaCheckCircle className="completion-badge complete" /> : <div className="completion-badge incomplete" />}
              {activeSection === 'coverLetter' ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </div>
          </div>
          {activeSection === 'coverLetter' && (
            <div className="section-content">
              {hasCoverLetterAccess ? (
                <>
                  <MobileCoverLetterGen
                    formData={formData}
                    handleInputChange={onInputChange}
                    handleCoverLetterGenerate={handleCoverLetterGenerate}
                    isLoading={isLoading}
                  />
                  {renderSectionNavFooter('coverLetter')}
                </>
              ) : (
                <div className="premium-lock-gate">
                  <div className="lock-icon">🔒</div>
                  <h3>Unlock AI Cover Letter Generator</h3>
                  <p className="description">
                    Tailor high-converting, professional cover letters matched perfectly to your resume and any job description in seconds.
                  </p>
                  <ul className="benefits-list">
                    <li>
                      <span className="benefit-icon">✨</span>
                      <span><strong>Instant Tailoring:</strong> Automatically align your experience with job requirements.</span>
                    </li>
                    <li>
                      <span className="benefit-icon">✍️</span>
                      <span><strong>Professional Copy:</strong> Write with the perfect tone and vocabulary for target employers.</span>
                    </li>
                    <li>
                      <span className="benefit-icon">🚀</span>
                      <span><strong>Stand Out:</strong> Candidates who submit cover letters get 40% more interview requests.</span>
                    </li>
                  </ul>
                  <button 
                    className="premium-upgrade-btn" 
                    onClick={() => {
                      if (typeof onClose === 'function') onClose();
                      navigate('/pricing');
                    }}
                  >
                    Upgrade to Professional / Ultimate
                  </button>
                </div>
              )}
            </div>
          )}

          {errorMessage && (
            <Popup
              message={errorMessage}
              onClose={() => setErrorMessage('')}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MobileEditPanel;